import type { $TSFixMe } from '@readme/iso';
import type Oas from 'oas';

import React, { useContext, useMemo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { ProjectContextValue, VariablesContextValue } from '@core/context';
import { UserContext, ProjectContext, VariablesContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import { useReferenceStore, useSuperHubStore } from '@core/store';
import { formatAbsoluteTime } from '@core/utils/i18nDateTime';

import useAPIKeys from '@routes/Reference/hooks/useAPIKeys';

import SectionHeader from '@ui/API/SectionHeader';
import Box from '@ui/Box';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import ObfuscatedAPIKey from '@ui/ObfuscatedAPIKey';
import Skeleton from '@ui/Skeleton';
import Sparkline from '@ui/Sparkline';
import Table from '@ui/Table';
import Tooltip from '@ui/Tooltip';

import UserAvatar from '../UserAvatar';

import classes from './style.module.scss';

interface Props {
  oas: Oas;
}

function YourAPIKeys({ oas }: Props) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'YourAPIKeys');
  const { isAdmin } = useContext(UserContext) as $TSFixMe;
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const { user } = useContext(VariablesContext) as VariablesContextValue;
  const [group, groups, updateGroup, isGroupLoggedIn] = useReferenceStore(s => [
    s.auth.group,
    s.auth.groups,
    s.auth.updateGroup,
    s.auth.isGroupLoggedIn,
  ]);

  const [isSuperHub, language] = useSuperHubStore(s => [s.isSuperHub, s.language]);

  const { pathname } = useLocation();
  const { keysSortedByLastUsed } = useAPIKeys({ fetchTrendData: true });

  const isLoggedIn = Boolean(groups && groups.length) || isGroupLoggedIn;

  const onAuthGroupChange = useCallback(
    groupId => {
      updateGroup({ apiDefinition: oas, groupId, user });
    },
    [oas, updateGroup, user],
  );

  const tableHead = [[t('auth.apiKey'), t('auth.label'), [t('auth.lastUsed'), 0, 'left', bem('-lastSeen')], '']];
  const tableData = useMemo(
    () =>
      keysSortedByLastUsed?.map(({ apiKey, dataSet, name, labels, lastUsed }, i) => {
        return [
          <ObfuscatedAPIKey
            key={`${i}-apikey`}
            apiKey={apiKey}
            className={bem('-ObfuscatedAPIKey')}
            conceal={'before'}
          />,
          <p key={`${i}-label`} className={bem('-overflow')}>
            <Tooltip content={name}>
              <span>{name}</span>
            </Tooltip>
          </p>,
          <Flex key={`${i}-lastSeen`} align="center" className={bem('-lastSeen')} gap="md" justify="start">
            <span className={bem('-lastSeen-date')}>
              {lastUsed
                ? formatAbsoluteTime(new Date(lastUsed), language, {
                    dateStyle: 'short',
                  })
                : '-'}
            </span>

            <div className={bem('-lastSeen-sparkline')}>
              <Sparkline
                data={(dataSet?.data || {}) as Record<string, number[]>}
                height="15px"
                labels={labels || []}
                maxWidth="80px"
              />
            </div>
          </Flex>,
          <>
            {group === apiKey ? (
              <i key={`${i}-apikey-check`} className={bem('-iconCheck', 'icon-check1')} />
            ) : (
              <Tooltip content="Use API Key">
                <Button
                  key={`${i}-apikey-button`}
                  className={bem('-button')}
                  ghost
                  kind={'minimum'}
                  onClick={() => onAuthGroupChange(apiKey)}
                  size={'xs'}
                >
                  <i className="icon-check1" />
                </Button>
              </Tooltip>
            )}
          </>,
        ];
      }),
    [keysSortedByLastUsed, bem, language, group, onAuthGroupChange],
  );

  if (!isLoggedIn) {
    return (
      <Flex className={bem('&')} gap="sm" layout="col">
        <Button
          className={bem('-empty-button')}
          href={`/login?redirect_uri=${pathname}`}
          kind="secondary"
          size="xs"
          text
        >
          <Icon aria-hidden="true" name="key" />
          {t('apiConfig.logInPrompt')}
        </Button>
        <Box className={bem('-box')} kind="rule">
          <Table
            body={[
              [
                /* eslint-disable react/jsx-key */
                <Skeleton height={10} width={85} />,
                <Skeleton height={10} width={85} />,
                <Skeleton height={10} width={60} />,
                <></>,
              ],

              [
                <Skeleton height={10} width={85} />,
                <Skeleton height={10} width={85} />,
                <Skeleton height={10} width={60} />,
                <></>,
                /* eslint-enable react/jsx-key */
              ],
            ]}
            className={bem('-table', '-table_empty')}
            head={tableHead}
          />
        </Box>
      </Flex>
    );
  }

  return (
    <section className={bem('&')}>
      <SectionHeader
        heading={
          <Flex align="center" gap="xs" justify="start">
            <UserAvatar /> {t('apiConfig.yourApiKeys')}
          </Flex>
        }
      />
      <Box className={bem('-box')} kind="rule">
        {keysSortedByLastUsed?.length ? (
          <Table body={tableData} className={bem('-table')} head={tableHead} />
        ) : (
          <Flex align="center" className={bem('-empty-prompt')} gap="sm" justify="center">
            <Icon aria-hidden="true" className={bem('-empty-prompt-icon')} name="key" />
            {!!project.webhookEnabled && <span>{t('apiConfig.apiKeysNotFound')}</span>}
            {!isAdmin && !project.webhookEnabled && <span>{t('apiConfig.apiKeysNotSynced')}</span>}
            {!!isAdmin && !project.webhookEnabled && (
              <span>
                {isSuperHub ? (
                  <Trans
                    components={{
                      Button: (
                        <Button
                          link
                          onClick={() => {
                            window.location.hash = '/settings/personalized-docs/setup';
                          }}
                          style={{
                            color: 'inherit',
                          }}
                        />
                      ),
                    }}
                    i18nKey="apiConfig.personalizedDocsSetup"
                  />
                ) : (
                  <>
                    Set up Personalized Docs in your <a href="https://dash.readme.com/">dashboard</a> to show users
                    their API keys.
                  </>
                )}
              </span>
            )}
          </Flex>
        )}
      </Box>
    </section>
  );
}

export default YourAPIKeys;
